import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { BusinessImage } from '../data/businesses';
interface LightboxProps {
  images: BusinessImage[];
  initialIndex: number;
  isOpen: boolean;
  onClose: () => void;
}
export function Lightbox({
  images,
  initialIndex,
  isOpen,
  onClose
}: LightboxProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, initialIndex]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex]);
  const handlePrev = () => {
    setCurrentIndex(prev => prev === 0 ? images.length - 1 : prev - 1);
  };
  const handleNext = () => {
    setCurrentIndex(prev => prev === images.length - 1 ? 0 : prev + 1);
  };
  return <AnimatePresence data-id="element-151">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.3
    }} className="fixed inset-0 z-[70] bg-black/95 flex items-center justify-center backdrop-blur-sm" data-id="element-152">
          {/* Close Button */}
          <button onClick={onClose} className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors p-2 z-50" aria-label="Close lightbox" data-id="element-153">
            <XIcon className="w-8 h-8" data-id="element-154" />
          </button>

          {/* Navigation */}
          <button onClick={e => {
        e.stopPropagation();
        handlePrev();
      }} className="absolute left-4 md:left-8 text-white/50 hover:text-white transition-colors p-4 z-50" aria-label="Previous image" data-id="element-155">
            <ChevronLeftIcon className="w-10 h-10" data-id="element-156" />
          </button>

          <button onClick={e => {
        e.stopPropagation();
        handleNext();
      }} className="absolute right-4 md:right-8 text-white/50 hover:text-white transition-colors p-4 z-50" aria-label="Next image" data-id="element-157">
            <ChevronRightIcon className="w-10 h-10" data-id="element-158" />
          </button>

          {/* Image Container */}
          <div className="relative w-full h-full flex items-center justify-center p-4 md:p-12" data-id="element-159">
            <AnimatePresence mode="wait" data-id="element-160">
              <motion.div key={currentIndex} initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} transition={{
            duration: 0.3
          }} className="relative max-w-full max-h-full" data-id="element-161">
                <img src={images[currentIndex].url} alt={images[currentIndex].alt} className="max-w-full max-h-[85vh] object-contain mx-auto shadow-2xl" data-id="element-162" />
                {images[currentIndex].caption && <div className="absolute bottom-0 left-0 right-0 bg-black/60 backdrop-blur-md p-4 text-center" data-id="element-163">
                    <p className="text-white text-[15px] font-medium tracking-wide" data-id="element-164">
                      {images[currentIndex].caption}
                    </p>
                  </div>}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Counter */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/60 text-[14px] font-medium tracking-widest uppercase" data-id="element-165">
            {currentIndex + 1} / {images.length}
          </div>
        </motion.div>}
    </AnimatePresence>;
}